import React, { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AdminTariffService } from '@/shared/api'
import type { Column, TariffDetailsDTO, TariffListItemDTO } from '@/shared/api'
import { useTariffs, useTariffMutation } from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { Button, CreateModal, DataTable, PageHeader } from '@/shared/ui'
import type { FormField } from '@/shared/ui'

const SCOPE_LABELS: Record<number, string> = { 1: 'Общий', 2: 'Индивидуальный' }
const STATUS_LABELS: Record<number, string> = { 0: 'Черновик', 1: 'Активен' }

// Общий тариф — один и создаётся системой; вручную создаём только индивидуальные.
// Курьер назначается позже — поиском в карточке тарифа.
const EMPTY_FORM = { name: '', description: '', baseRate: 0 }

interface CreateTariffInput {
  name: string
  description: string | null
  scope: number
  courier_id: number | null
  version: { base_rate: number }
}

export const TariffsPage: React.FC = () => {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const [scope, setScope] = useState<number | undefined>(undefined)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)

  const { data, isLoading } = useTariffs(scope ? { scope } : undefined)

  const createTariff = useTariffMutation<CreateTariffInput>(
    (payload) => AdminTariffService.create(payload),
    'Тариф создан'
  )

  const handleCreate = async () => {
    if (!form.name.trim()) return
    try {
      const created = (await createTariff.mutateAsync({
        name: form.name.trim(),
        description: form.description.trim() ? form.description.trim() : null,
        scope: 2,
        courier_id: null,
        version: { base_rate: form.baseRate },
      })) as TariffDetailsDTO
      setShowCreate(false)
      setForm(EMPTY_FORM)
      navigate({
        to: '/tariffs/$tariffId',
        params: { tariffId: String(created.tariff.courier_tariff_id) },
      })
    } catch {
      // Ошибка уже показана тостом в useTariffMutation
    }
  }

  const createFields: FormField[] = [
    {
      name: 'name',
      label: 'Название',
      type: 'text',
      required: true,
      placeholder: 'Например, Индивидуальный тариф Иванова',
      value: form.name,
      onChange: (value) => setForm((prev) => ({ ...prev, name: String(value) })),
    },
    {
      name: 'description',
      label: 'Описание',
      type: 'textarea',
      value: form.description,
      onChange: (value) => setForm((prev) => ({ ...prev, description: String(value) })),
    },
    {
      name: 'baseRate',
      label: 'Базовая ставка',
      type: 'number',
      value: form.baseRate,
      onChange: (value) => setForm((prev) => ({ ...prev, baseRate: Number(value) })),
    },
  ]

  const columns: Column<TariffListItemDTO>[] = [
    { key: 'name', title: 'Название' },
    { key: 'scope', title: 'Область', render: (_, row) => SCOPE_LABELS[row.scope] ?? '—' },
    { key: 'courier_id', title: 'Курьер', render: (_, row) => row.courier_id ?? '—' },
    { key: 'base_rate', title: 'Базовая ставка', render: (_, row) => row.base_rate ?? '—' },
    { key: 'version_number', title: 'Версия', render: (_, row) => row.version_number ?? '—' },
    { key: 'status', title: 'Статус', render: (_, row) => STATUS_LABELS[row.status] ?? '—' },
  ]

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Тарифы курьеров"
        description="Тарифы вознаграждения сторонних курьеров"
      >
        {hasPermission('tariffs', 'create') && (
          <Button onClick={() => setShowCreate(true)}>Создать</Button>
        )}
      </PageHeader>

      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={scope === undefined ? 'primary' : 'secondary'}
            onClick={() => setScope(undefined)}
          >
            Все
          </Button>
          <Button variant={scope === 1 ? 'primary' : 'secondary'} onClick={() => setScope(1)}>
            Общие
          </Button>
          <Button variant={scope === 2 ? 'primary' : 'secondary'} onClick={() => setScope(2)}>
            Индивидуальные
          </Button>
        </div>

        <DataTable
          data={data?.items ?? []}
          columns={columns}
          loading={isLoading}
          emptyMessage="Тарифов нет"
          onRowClick={(row) =>
            navigate({
              to: '/tariffs/$tariffId',
              params: { tariffId: String(row.courier_tariff_id) },
            })
          }
        />
      </div>

      <CreateModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Создать индивидуальный тариф"
        fields={createFields}
        onSubmit={handleCreate}
        onCancel={() => setForm(EMPTY_FORM)}
        isLoading={createTariff.isPending}
      />
    </div>
  )
}
